import type { CanonicalOrderSnapshot } from '../order/order-finalization.ts';
import type { SettlementUnitInput } from './payment-authority.ts';

interface MutableUnit {
  readonly unitId: string;
  readonly lineId: string;
  readonly unitIndex: number;
  amountMinor: number;
}

const assertSafeNonNegative = (value: number, code: string): void => {
  if (!Number.isSafeInteger(value) || value < 0) throw new Error(code);
};

const distribute = (units: MutableUnit[], indexes: readonly number[], deltaMinor: number): void => {
  if (deltaMinor === 0) return;
  if (indexes.length === 0) throw new Error('ORDER_SETTLEMENT_TARGET_REQUIRED');
  if (!Number.isSafeInteger(deltaMinor)) throw new Error('ORDER_SETTLEMENT_ADJUSTMENT_INVALID');

  const magnitude = Math.abs(deltaMinor);
  const sign = deltaMinor < 0 ? -1 : 1;
  const weights = indexes.map((index) => units[index]!.amountMinor);
  const weightTotal = weights.reduce((sum, weight) => sum + weight, 0);
  const effectiveWeights = weightTotal === 0 ? weights.map(() => 1) : weights;
  const effectiveTotal = weightTotal === 0 ? effectiveWeights.length : weightTotal;

  if (sign < 0) {
    const available = indexes.reduce((sum, index) => sum + units[index]!.amountMinor, 0);
    if (magnitude > available) throw new Error('ORDER_SETTLEMENT_UNIT_NEGATIVE');
  }

  const shares = effectiveWeights.map((weight, position) => {
    const numerator = magnitude * weight;
    if (!Number.isSafeInteger(numerator)) throw new Error('ORDER_SETTLEMENT_ADJUSTMENT_INVALID');
    return {
      position,
      whole: Math.floor(numerator / effectiveTotal),
      remainder: numerator % effectiveTotal,
    };
  });

  let assigned = shares.reduce((sum, share) => sum + share.whole, 0);
  const remainderOrder = [...shares].sort((left, right) =>
    right.remainder - left.remainder || left.position - right.position,
  );
  for (let index = 0; assigned < magnitude; index += 1, assigned += 1) {
    remainderOrder[index % remainderOrder.length]!.whole += 1;
  }

  for (const share of shares) {
    const unit = units[indexes[share.position]!]!;
    unit.amountMinor += sign * share.whole;
    if (unit.amountMinor < 0) throw new Error('ORDER_SETTLEMENT_UNIT_NEGATIVE');
  }
};

export function buildOrderSettlementUnits(order: CanonicalOrderSnapshot): readonly SettlementUnitInput[] {
  const units: MutableUnit[] = [];
  const byIdentity = new Map<string, number>();

  for (const line of order.pricingLines) {
    if (!Number.isSafeInteger(line.quantity) || line.quantity <= 0) {
      throw new Error('ORDER_SETTLEMENT_QUANTITY_INVALID');
    }
    const unitAmount = line.unitBaseMinor + line.unitModifierMinor;
    assertSafeNonNegative(unitAmount, 'ORDER_SETTLEMENT_UNIT_AMOUNT_INVALID');
    if (line.lineSubtotalMinor !== unitAmount * line.quantity) {
      throw new Error('ORDER_SETTLEMENT_PRICING_LINE_MISMATCH');
    }
    for (let unitIndex = 0; unitIndex < line.quantity; unitIndex += 1) {
      const identity = `${line.lineId}:${unitIndex}`;
      if (byIdentity.has(identity)) throw new Error('ORDER_SETTLEMENT_UNIT_DUPLICATE');
      const index = units.length;
      byIdentity.set(identity, index);
      units.push({
        unitId: `${order.orderId}:${line.lineId}:${unitIndex}`,
        lineId: line.lineId,
        unitIndex,
        amountMinor: unitAmount,
      });
    }
  }

  if (units.length === 0) {
    if (order.totalMinor !== 0) throw new Error('ORDER_SETTLEMENT_UNITS_REQUIRED');
    return [];
  }

  const allIndexes = units.map((_, index) => index);
  for (const adjustment of order.pricingAdjustments) {
    const targetIndexes = adjustment.consumedUnits === undefined
      ? allIndexes
      : adjustment.consumedUnits.map((unit) => {
        const index = byIdentity.get(`${unit.lineId}:${unit.unitIndex}`);
        if (index === undefined) throw new Error('ORDER_SETTLEMENT_ADJUSTMENT_TARGET_INVALID');
        return index;
      });
    if (new Set(targetIndexes).size !== targetIndexes.length) {
      throw new Error('ORDER_SETTLEMENT_ADJUSTMENT_TARGET_DUPLICATE');
    }
    distribute(units, targetIndexes, adjustment.amountMinor);
  }

  const currentTotal = units.reduce((sum, unit) => sum + unit.amountMinor, 0);
  assertSafeNonNegative(order.totalMinor, 'ORDER_SETTLEMENT_ORDER_TOTAL_INVALID');
  distribute(units, allIndexes, order.totalMinor - currentTotal);

  const acceptedTotal = units.reduce((sum, unit) => sum + unit.amountMinor, 0);
  if (acceptedTotal !== order.totalMinor) throw new Error('ORDER_SETTLEMENT_TOTAL_MISMATCH');

  return units.map((unit) => ({
    unitId: unit.unitId,
    lineId: unit.lineId,
    unitIndex: unit.unitIndex,
    amountMinor: unit.amountMinor,
  }));
}
